import { Check, Code2, Copy, Facebook, Image as ImageIcon, Instagram, Link2, Linkedin, Mail, MessageCircle, MessageSquare } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import ReferralLinkBox from '@/components/member/ReferralLinkBox';
import Banner125x125 from '@/components/member/widgets/Banner125x125';
import Banner468x60 from '@/components/member/widgets/Banner468x60';
import Banner728x90 from '@/components/member/widgets/Banner728x90';
import { buildEmbed125x125, buildEmbed468x60, buildEmbed728x90 } from '@/components/member/widgets/banner_embeds';
import MARKETING_TEMPLATES from '@/components/member/widgets/marketing_templates';

/**
 * PromoSubpage — member-facing "Promote" page.
 *
 * Each banner block exposes THREE copyable assets:
 *   1) HTML embed code (animated SVG/CSS — drops anywhere)
 *   2) Static JPEG image URL (hosted on the custom domain; same words
 *      as the animated banner, frozen)
 *   3) The user's personal referral link
 *
 * Below the banners, eight ready-to-copy marketing templates (sms/email
 * cold+warm + facebook/instagram/reddit/linkedin) come pre-substituted
 * with the user's referral link.
 */

// Production custom domain — hardcoded so the URLs shown on this page
// (image URLs, referral links, embed logo refs) ALWAYS reflect the
// real domain users will be sharing, regardless of which environment
// (preview vs. production) the SPA is currently rendered on.
const BRAND_ORIGIN = 'https://1simplecycler3.com';

const ICONS = {
  MessageSquare,
  Mail,
  Facebook,
  Instagram,
  MessageCircle,
  Linkedin,
};

// -------- Shared copy helpers --------
const CopyButton = ({ value, label = 'Copy', testid }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed — select the text and copy manually');
    }
  };
  return (
    <button type="button" onClick={handle} className="sc-btn-ghost shrink-0" style={{ padding: '8px 12px', fontSize: 11 }} data-testid={testid}>
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const CopyRow = ({ icon: Icon, label, value, testidPrefix, isCode = false }) => {
  const fieldId = useId();
  return (
    <div className="mt-3" data-testid={`${testidPrefix}-row`}>
      <label htmlFor={fieldId} className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5 flex items-center gap-1.5">
        <Icon className="w-3 h-3" />
        {label}
      </label>
      <div className="flex items-stretch gap-2">
        {isCode ? (
          <textarea
            id={fieldId}
            readOnly
            value={value}
            onClick={(e) => e.currentTarget.select()}
            className="sc-tabular flex-1 px-3 py-2.5 rounded-lg bg-[rgba(0,0,0,0.7)] border border-[rgba(255,182,18,0.18)] text-[11px] text-[var(--sc-text-dim)] font-mono leading-relaxed h-28 focus:outline-none focus:border-[rgba(255,182,18,0.45)]"
            data-testid={`${testidPrefix}-value`}
          />
        ) : (
          <input
            id={fieldId}
            readOnly
            value={value}
            onClick={(e) => e.currentTarget.select()}
            className="sc-tabular flex-1 px-3 py-2.5 rounded-lg bg-[rgba(0,0,0,0.7)] border border-[rgba(255,182,18,0.18)] text-[11.5px] text-[var(--sc-text)] font-mono focus:outline-none focus:border-[rgba(255,182,18,0.45)]"
            data-testid={`${testidPrefix}-value`}
          />
        )}
        <CopyButton value={value} label={label} testid={`${testidPrefix}-copy`} />
      </div>
    </div>
  );
};

// -------- Banner block (animated preview + 3 copy rows) --------
const BannerBlock = ({ title, dims, preview, embedCode, imageUrl, refLink, testid }) => (
  <section className="sc-glass rounded-2xl p-5 md:p-6" data-testid={testid}>
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{dims}</div>
        <h3 className="font-display font-bold text-lg md:text-xl mt-1">{title}</h3>
      </div>
    </div>
    <div className="flex justify-center py-4 px-2 rounded-xl bg-[rgba(0,0,0,0.55)] border border-[rgba(255,182,18,0.08)] overflow-x-auto">{preview}</div>

    {/* Three copyable lines */}
    <CopyRow icon={Code2} label="HTML / CSS Embed (animated)" value={embedCode} testidPrefix={`${testid}-embed`} isCode />
    <CopyRow icon={ImageIcon} label="Static JPEG Image URL" value={imageUrl} testidPrefix={`${testid}-imageurl`} />
    <CopyRow icon={Link2} label="Your Referral Link" value={refLink} testidPrefix={`${testid}-reflink`} />
  </section>
);

// -------- Marketing template card --------
const TemplateCard = ({ template, refLink }) => {
  const rendered = useMemo(() => template.body(refLink), [template, refLink]);
  const Icon = ICONS[template.icon] || MessageSquare;
  return (
    <div className="sc-glass rounded-2xl p-5 flex flex-col gap-3" data-testid={`template-${template.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: 'rgba(255,194,14,0.10)',
              border: '1px solid rgba(255,194,14,0.32)',
            }}
          >
            <Icon className="w-4 h-4 text-[#FFC20E]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{template.audience}</div>
            <div className="font-display font-semibold text-[14px] text-white truncate">{template.channel}</div>
          </div>
        </div>
        <CopyButton value={rendered} label={template.channel} testid={`template-${template.id}-copy`} />
      </div>
      <pre
        className="whitespace-pre-wrap text-[11.5px] text-[var(--sc-text-dim)] leading-relaxed font-sans rounded-lg p-3 border border-[rgba(255,182,18,0.10)] bg-[rgba(0,0,0,0.42)] max-h-56 overflow-y-auto"
        data-testid={`template-${template.id}-body`}
      >
        {rendered}
      </pre>
    </div>
  );
};

// -------- Page --------
const PromoSubpage = ({ me }) => {
  // Bare-domain referral link used INSIDE the animated banner anchor
  // (kept short for embed compactness).
  const refLinkBare = useMemo(() => `1simplecycler3.com/${me.referral_code}`, [me.referral_code]);

  // Full https:// referral link — canonical shareable form for marketing
  // templates and the per-banner "Your Referral Link" copy row.
  const refLinkFull = useMemo(() => `${BRAND_ORIGIN}/${me.referral_code}`, [me.referral_code]);

  // Static JPEG URLs — locked to the production custom domain so the
  // user copies real URLs even while previewing.
  const imageUrl728 = `${BRAND_ORIGIN}/banners/728x90.jpg`;
  const imageUrl468 = `${BRAND_ORIGIN}/banners/468x60.jpg`;
  const imageUrl125 = `${BRAND_ORIGIN}/banners/125x125.jpg`;

  return (
    <div className="space-y-6" data-testid="subpage-promo">
      <ReferralLinkBox referralCode={me.referral_code} label="Promote With This Link" />

      <BannerBlock
        testid="banner-728x90"
        title="Leaderboard Banner"
        dims="728 × 90 · Marquee + Pulsing Brand + Flashing CTA"
        preview={<Banner728x90 refLink={refLinkBare} />}
        embedCode={buildEmbed728x90(refLinkBare)}
        imageUrl={imageUrl728}
        refLink={refLinkFull}
      />

      <BannerBlock
        testid="banner-468x60"
        title="Full Banner"
        dims="468 × 60 · Black/Gold High-Contrast Flash"
        preview={<Banner468x60 refLink={refLinkBare} />}
        embedCode={buildEmbed468x60(refLinkBare)}
        imageUrl={imageUrl468}
        refLink={refLinkFull}
      />

      <BannerBlock
        testid="banner-125x125"
        title="Square Button"
        dims="125 × 125 · Dashed Border + Vertical Bounce"
        preview={<Banner125x125 refLink={refLinkBare} />}
        embedCode={buildEmbed125x125(refLinkBare)}
        imageUrl={imageUrl125}
        refLink={refLinkFull}
      />

      {/* -------- Preset Marketing Templates -------- */}
      <section className="sc-glass rounded-2xl p-5 md:p-6" data-testid="marketing-templates">
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Preset Marketing Templates</div>
          <h3 className="font-display font-bold text-lg md:text-xl mt-1">Ready-to-Post Copy</h3>
          <p className="text-[12px] text-[var(--sc-text-dim)] mt-1.5 leading-relaxed max-w-2xl">
            Eight templates tuned for the channel they're written for — your referral link is already inside every one. Click any <strong>Copy</strong> button
            and paste into your phone, inbox, or feed.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MARKETING_TEMPLATES.map((t) => (
            <TemplateCard key={t.id} template={t} refLink={refLinkFull} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default PromoSubpage;
